/**
 * ScanCompletionNotificationService - Desktop/in-app notification handler for completed scans
 *
 * Boundary: Does NOT monitor scans - only handles notification display when triggered by ScanStatusMonitorService
 * External dependencies:
 * - ScanStatusMonitorService: Provides scan completion events stream
 * - ToastNotificationService: Desktop notification API
 * - NotificationManagerService: In-app notification UI
 * - Router: Navigation to scan details page
 */
import { Injectable, inject } from '@angular/core';
import { ToastNotificationService } from './toast-notification.service';
import { NotificationManagerService } from './notification-manager.service';
import {
  ScanStatusMonitorService,
  ScanStatusChange,
} from './scan-status-monitor.service';
import { Router } from '@angular/router';
import { LoggingService } from './logging.service';

export interface ScanNotificationOptions {
  showDesktopNotification?: boolean;
  showInAppNotification?: boolean;
  autoNavigateToScan?: boolean;
  customTitle?: string;
  customMessage?: string;
}

/**
 * Displays scan completion notifications with navigation and permission management
 *
 * Dependencies:
 * - ToastNotificationService: Desktop notification system with permission requests
 * - NotificationManagerService: In-app notification UI
 * - ScanStatusMonitorService: Scan completion event source
 * - Router: Navigate to /dashboard/scans/view/{scanId}
 * - LoggingService: Error tracking
 *
 * Side effects:
 * - Desktop notifications: Sent via browser Notification API (requires permission)
 * - In-app notifications: Displayed via NotificationManagerService
 * - Navigation: Router.navigate() when user clicks notification or autoNavigateToScan=true
 * - Subscription: Listens to scanStatusMonitor.getCompletionChanges() when startListening() called
 */
@Injectable({
  providedIn: 'root',
})
export class ScanCompletionNotificationService {
  private toastNotificationService = inject(ToastNotificationService);
  private notificationManager = inject(NotificationManagerService);
  private loggingService = inject(LoggingService);
  private scanStatusMonitor = inject(ScanStatusMonitorService);
  private router = inject(Router);

  private isListening = false;

  /**
   * Subscribes to scan completion events and displays notifications
   *
   * @param options - Notification configuration (desktop/in-app, auto-navigate, custom text)
   * @returns void
   * - State touched: Sets isListening=true, subscribes to scanStatusMonitor.getCompletionChanges()
   * - Idempotency: Safe to call multiple times (early return if already listening)
   * - Defaults: Desktop notifications ON, in-app OFF, auto-navigate OFF
   * - Side effects: Subscribes to Observable (cleaned up by Angular lifecycle)
   */
  startListening(options: ScanNotificationOptions = {}): void {
    if (this.isListening) {
      return;
    }

    const defaultOptions: ScanNotificationOptions = {
      showDesktopNotification: true,
      showInAppNotification: false, // Desktop first, in-app as fallback
      autoNavigateToScan: false,
      customTitle: undefined,
      customMessage: undefined,
      ...options,
    };

    // Subscribe to scan completion events
    this.scanStatusMonitor.getCompletionChanges().subscribe((completions) => {
      if (completions.length > 0) {
        completions.forEach((completion) => {
          this.handleScanCompletion(completion, defaultOptions);
        });
      }
    });

    this.isListening = true;
  }

  /**
   * Stop listening for scan completions
   * @returns void
   */
  stopListening(): void {
    this.isListening = false;
    // Note: The observable subscription will be handled by Angular's lifecycle
  }

  /**
   * Check if currently listening for notifications
   * @returns boolean - True if currently listening for scan completion notifications
   */
  isCurrentlyListening(): boolean {
    return this.isListening;
  }

  /**
   * Handle a scan completion event
   * @param completion - The scan status change event containing completion details
   * @param options - Configuration options for the notification
   * @returns void
   */
  private handleScanCompletion(
    completion: ScanStatusChange,
    options: ScanNotificationOptions,
  ): void {
    const _title = options.customTitle || 'Scan Complete';
    const _message =
      options.customMessage ||
      `<span class="text-primary"><strong>${completion.name}</strong></span> has finished scanning and results are ready to review.`;

    // Create navigation action
    const _viewScanAction = {
      label: 'View Scan Results',
      callback: () => {
        this.navigateToScan(completion.scanId);
      },
    };

    // Show desktop notification (primary method)
    if (options.showDesktopNotification) {
      // TODO @tell figure out what this is for??
      // const notificationId = this.toastNotificationService.showSuccess(
      //   title,
      //   message,
      //   0, // Permanent - requires manual dismissal
      //   viewScanAction
      // );

      // Auto-navigate if requested
      if (options.autoNavigateToScan) {
        setTimeout(() => {
          this.navigateToScan(completion.scanId);
        }, 2000); // 2 second delay to let user see notification
      }
    }

    // Show in-app notification if requested (or as fallback)
    if (options.showInAppNotification) {
      this.notificationManager.showScanCompletion(
        completion.name,
        `Scan completed successfully.`,
      );
    }

    // Log completion metrics
    this.logCompletionMetrics(completion);
  }

  /**
   * Navigate to scan details page
   * @param scanId - The unique identifier of the scan to navigate to
   * @returns void
   */
  private navigateToScan(scanId: string): void {
    try {
      // Navigate to scan view page
      this.router.navigate(['/dashboard/scans/view', scanId]);
    } catch (error) {
      this.loggingService.logError('Navigation failed:', error);

      // Fallback to scans list
      this.router.navigate(['/dashboard/scans']);
    }
  }

  /**
   * Requests browser permission for desktop notifications (one-time prompt)
   *
   * @returns Promise<boolean> resolving to true if permission granted, false otherwise
   * - Browser API: Triggers Notification.requestPermission() dialog
   * - Persistence: Permission persists across sessions once granted
   * - Use case: Call during onboarding or before first notification attempt
   */
  async requestNotificationPermissions(): Promise<boolean> {
    const isGranted =
      await this.toastNotificationService.requestNotificationPermission();
    return isGranted;
  }

  /**
   * Check if desktop notifications are supported and permitted
   * @returns boolean - True if desktop notifications are both supported and permitted
   */
  canShowDesktopNotifications(): boolean {
    const isSupported =
      this.toastNotificationService.isDesktopNotificationSupported();
    const isPermitted =
      this.toastNotificationService.getNotificationPermission() === 'granted';

    return isSupported && isPermitted;
  }

  /**
   * Initializes notification system with permission check and request
   *
   * @returns Promise<boolean> resolving to true if desktop notifications ready, false if unsupported/denied
   * - Checks: Browser support via Notification API availability
   * - Permission: Auto-requests if not already granted
   * - Error handling: Logs warnings, returns false (does not throw)
   * - Use case: Call during app initialization or before monitoring starts
   */
  async initialize(): Promise<boolean> {
    // Check support
    if (!this.toastNotificationService.isDesktopNotificationSupported()) {
      this.loggingService.logWarning(
        'Desktop notifications not supported in this browser',
      );
      return false;
    }

    // Request permissions
    const hasPermission = await this.requestNotificationPermissions();

    if (!hasPermission) {
      this.loggingService.logWarning('Desktop notification permission denied');
      return false;
    }

    return true;
  }

  /**
   * Log completion metrics for monitoring
   * @param completion - The scan status change event to log metrics for
   * @returns void
   */
  private logCompletionMetrics(completion: ScanStatusChange): void {
    const _timeToCompletion = this.calculateTimeToCompletion(completion);
  }

  /**
   * Calculate time to completion
   * @param completion - The scan status change event containing timestamp information
   * @returns string - Human-readable duration string or completion time
   */
  private calculateTimeToCompletion(completion: ScanStatusChange): string {
    if (!completion.timestamp) {
      return 'Unknown - no timestamp available';
    }

    // If scan has a startTime, calculate duration from start to completion
    if (completion.timestamp) {
      const startTime = new Date(completion.timestamp);
      const endTime = new Date(completion.timestamp);
      const durationMs = endTime.getTime() - startTime.getTime();

      return this.formatDuration(durationMs);
    }

    // Fallback: just show when it completed
    const completedAt = new Date(completion.timestamp);
    return `Completed at ${completedAt.toLocaleTimeString()}`;
  }

  /**
   * Format duration in milliseconds to human-readable string
   * @param durationMs - Duration in milliseconds to format
   * @returns string - Formatted duration string (e.g., "2h 30m", "45m 12s", "30s")
   */
  private formatDuration(durationMs: number): string {
    const seconds = Math.floor(durationMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      const remainingMinutes = minutes % 60;
      return `${hours}h ${remainingMinutes}m`;
    } else if (minutes > 0) {
      const remainingSeconds = seconds % 60;
      return `${minutes}m ${remainingSeconds}s`;
    } else {
      return `${seconds}s`;
    }
  }

  /**
   * Displays single notification for multiple completed scans (batch notification)
   *
   * @param completions - Array of ScanStatusChange events to summarize
   * @returns void
   * - Message: "N Scans Complete" with comma-separated scan names
   * - Action button: "View All Scans" navigates to /dashboard/scans
   * - Display: Permanent (requires manual dismissal)
   * - Use case: When multiple scans complete simultaneously, avoid notification spam
   */
  showBulkCompletionNotification(completions: ScanStatusChange[]): void {
    const title = `${completions.length} Scans Complete`;
    const scanNames = completions.map((c) => c.name).join(', ');
    const message =
      completions.length === 1
        ? `${scanNames} has finished scanning.`
        : `Multiple scans have finished: ${scanNames}`;

    const viewAllAction = {
      label: 'View All Scans',
      callback: () => {
        this.router.navigate(['/dashboard/scans']);
      },
    };

    // Show desktop notification
    this.notificationManager.showSuccess(
      title,
      message,
      0, // Permanent
      viewAllAction,
    );
  }
}
